// @ts-check
import React, { useState } from 'react';
import { Box, makeStyles, Button } from '@material-ui/core';
import { useConfirmation, UseConfirmationOptions } from './confirmation';
import { omit } from '../util/objects';
import { EditCardButton } from '../components/Card';
import { EditCardButtonProps } from '../components/Card/EditCardButton';

const useStyles = makeStyles(theme => ({
    box: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    button: {
        marginLeft: theme.spacing(1),
    },
}));

type UseEditModeLabels = UseConfirmationOptions & {
    confirmationMessage: string;
    confirmationTitle?: string;
    discardLabel: string;
    saveLabel: string;
}

type UseEditModeResponse<T> = {
    /**
     * Whether editing mode is currently active
     */
    active: boolean;
    /**
     * Enable the editing mode
     */
    enable(): void;
    /**
     * Disable the editing mode
     * @param ignoreDirty Skips the confirmation of discarding dirty changes
     * @return The value indicates whether the edit mode was successfully
     *      disabled, i.e. it isn't still active after not discarding dirty
     *      changes without `ignoreDirty` set.
     */
    disable(ignoreDirty?: boolean): Promise<boolean>;
    /**
     * Set the new state depending on the current state with a callback function
     * of directly with a non-function value.
     */
    set(setter: T|((cb: T) => T)): void;
    /**
     * The current state
     */
    state: T;
    /**
     * Reset the changes to the initial state
     */
    reset(): void;
    /**
     * Updates the internal fallback state to the current state
     */
    commit(): void;
    /**
     * Can be used to re-initialize the edit mode after data finished loading
     */
    update(newState: T): void;
    /**
     * Whether there are non-committed changes in the edit mode
     * @see commit
     */
    dirty: boolean;
    /**
     * Renders the buttons to submit or cancel the edit mode
     * @param onSubmit The function to call
     * @param disabled
     */
    renderEditingButtons(onSubmit: () => void, disabled?: boolean): JSX.Element;
    /**
     * Renders a button to start the edit mode
     * @param props
     */
    renderTriggerButton(props: Omit<EditCardButtonProps, 'onClick'>): JSX.Element;
    /**
     * Needs to be placed to render the confirmation dialog asking to discard
     * changes
     */
    renderConfirmationDialog(): JSX.Element;
}

export const useEditMode = <T,>(initialData: T, labels: UseEditModeLabels): UseEditModeResponse<T> => {
    const classes = useStyles({});
    const confirmationOptions = omit(labels, [ 'discardLabel', 'saveLabel' ]);
    const { render: renderConfirmationDialog, confirm } = useConfirmation(confirmationOptions);
    const [ active, setActive ] = useState(false);
    const [ dirty, setDirty ] = useState(false);
    const [ initialState, setInitialState ] = useState(initialData);
    const [ state, setState ] = useState(initialData);

    const reset = () => {
        setState(initialState);
        setDirty(false);
    };

    const commit: UseEditModeResponse<T>['commit'] = () => {
        setInitialState(state);
    };

    const update: UseEditModeResponse<T>['update'] = (newState) => {
        setState(newState);
        setInitialState(newState);
        setDirty(false);
    };

    /**
     * Set the new state dependeing on the current state with a callback function
     * of directly with a non-function value.
     * @param {T|((cb: T) => T)} setter
     */
    const set: UseEditModeResponse<T>['set'] = (setter) => {
        const newState = setter instanceof Function ? setter(state) : setter;
        setState(newState);
        setDirty(JSON.stringify(newState) !== JSON.stringify(state));
    };

    const enable: UseEditModeResponse<T>['enable'] = () => {
        setActive(true);
    };

    const disable: UseEditModeResponse<T>['disable'] = async (ignoreDirty = false) => {
        if (!dirty || ignoreDirty || await confirm(labels.confirmationMessage, labels.confirmationTitle || '')) {
            setActive(false);
            return true;
        }
        return false;
    };

    const discard = () => {
        (async () => {
            if (await disable()) {
                reset();
            }
        })();
    };

    const renderEditingButtons: UseEditModeResponse<T>['renderEditingButtons'] = (onSubmit, disabled = false) => active ? (
        <Box className={classes.box}>
            <Button disabled={disabled} onClick={discard}>{labels.discardLabel}</Button>
            <Button disabled={disabled} onClick={onSubmit} className={classes.button} color="primary" variant="contained">{labels.saveLabel}</Button>
            {renderConfirmationDialog()}
        </Box>
    ) : null;

    const renderTriggerButton: UseEditModeResponse<T>['renderTriggerButton'] = (buttonProps) => active ? null : (
        <EditCardButton {...buttonProps} onClick={enable}/>
    );

    return {
        enable,
        disable,
        active,
        set,
        state,
        reset,
        commit,
        dirty,
        update,
        renderEditingButtons,
        renderTriggerButton,
        renderConfirmationDialog,
    };
};
